import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import multiplayerAPI from '../services/multiplayerAPI';
import '../styles/Multiplayer.css';

const MultiplayerGame = ({ user, socket }) => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState('lobby'); // lobby, rolling, complete
  const [challenge, setChallenge] = useState(null);
  const [playerRolls, setPlayerRolls] = useState({});
  const [readyPlayerIds, setReadyPlayerIds] = useState([]);
  const [turnDeadlineAt, setTurnDeadlineAt] = useState(null);
  const [turnSecondsLeft, setTurnSecondsLeft] = useState(null);
  const [myScore, setMyScore] = useState(null);
  const [diceValues, setDiceValues] = useState([]);
  const [isRevealing, setIsRevealing] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [connectionBanner, setConnectionBanner] = useState('');
  const [loadingChallenge, setLoadingChallenge] = useState(true);
  const [playerOrder, setPlayerOrder] = useState([]);
  const [currentPlayerTurn, setCurrentPlayerTurn] = useState(null);
  const isMountedRef = useRef(false);
  const playerOrderRef = useRef([]);
  const gameStateRef = useRef('lobby');
  const wasDisconnectedRef = useRef(false);
  const revealTimerRef = useRef(null);
  const currentTurnIndex = currentPlayerTurn ? playerOrder.indexOf(currentPlayerTurn) : -1;
  const isMyTurn = currentPlayerTurn === user.id;
  const isBettingGame = Number(challenge?.wager || 0) > 0;

  const participantsById = React.useMemo(() => {
    const map = {};
    const participants = challenge?.participants;
    if (Array.isArray(participants)) {
      participants.forEach(p => {
        if (p && typeof p.id !== 'undefined') {
          map[p.id] = p.username;
        }
      });
    }
    if (challenge?.creator_id && challenge?.creator_username) {
      map[challenge.creator_id] = challenge.creator_username;
    }
    return map;
  }, [challenge]);

  const getDisplayName = useCallback((playerId, fallbackLabel = 'Player') => {
    if (playerId === user.id) return 'You';
    const name = participantsById?.[playerId];
    return name || fallbackLabel;
  }, [participantsById, user.id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (revealTimerRef.current) {
        clearTimeout(revealTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  useEffect(() => {
    playerOrderRef.current = playerOrder;
  }, [playerOrder]);

  const fetchRolls = useCallback(async (prizes = null) => {
    try {
      const response = await multiplayerAPI.getRolls(challengeId);
      if (response && response.data && response.data.rolls && isMountedRef.current) {
        console.log(`📊 Fetched ${response.data.rolls.length} rolls for challenge ${challengeId}`);
        const sorted = [...response.data.rolls].sort((a, b) => b.score - a.score);

        const safePrizeMap = prizes && typeof prizes === 'object' ? prizes : null;
        setResults({
          placement: sorted.map((r, idx) => ({
            rank: idx + 1,
            userId: r.player_id,
            username: r.username,
            score: r.score,
            diceValues: r.dice_values,
            prize: safePrizeMap ? (safePrizeMap[r.player_id] ?? safePrizeMap[String(r.player_id)] ?? 0) : 0
          }))
        });

        const rollMap = {};
        response.data.rolls.forEach(r => {
          rollMap[r.player_id] = {
            score: r.score,
            diceValues: r.dice_values
          };
        });
        setPlayerRolls(rollMap);

        const order = playerOrderRef.current;
        if (order.length > 0) {
          const nextTurnIdx = response.data.rolls.length;
          setCurrentPlayerTurn(nextTurnIdx < order.length ? order[nextTurnIdx] : null);
        }

        const myRoll = response.data.rolls.find(r => r.player_id === user.id);
        if (myRoll) {
          setMyScore(myRoll.score);
          setDiceValues(myRoll.dice_values);
          console.log(`✓ Found my roll: ${myRoll.score}`);
        }
      } else {
        console.warn('Invalid rolls response:', response);
      }
    } catch (error) {
      console.error('Error fetching rolls:', error.response?.data || error.message);
    }
  }, [challengeId, user.id]);

  const fetchChallenge = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoadingChallenge(true);
      }
      const response = await multiplayerAPI.getChallenge(challengeId);
      
      if (response && response.data && response.data.challenge && isMountedRef.current) {
        const challengeData = response.data.challenge;
        console.log('🎮 Challenge data:', challengeData);
        setChallenge(challengeData);
        setReadyPlayerIds(Array.isArray(challengeData.readyPlayerIds) ? challengeData.readyPlayerIds : []);
        setTurnDeadlineAt(challengeData.turnDeadlineAt ?? null);

        const order = [challengeData.creator_id, ...(challengeData.opponent_ids || [])];
        setPlayerOrder(order);
        // Ensure subsequent roll fetches can compute next turn immediately
        playerOrderRef.current = order;

        if (challengeData.game_status === 'waiting') {
          setGameState('lobby');
          setCurrentPlayerTurn(null);
          setPlayerRolls({});
        } else if (challengeData.game_status === 'in_progress') {
          console.log('Game status changed to in_progress');
          setGameState('rolling');
          setMessage('Game is starting!');
          // Pull current rolls so we can derive current turn even if a socket event was missed
          await fetchRolls();
        } else if (challengeData.game_status === 'completed') {
          console.log('Game status changed to completed');
          setMessage('Game ended. Results below.');
          await fetchRolls(challengeData.prize_distribution);
          setGameState('complete');
        }
      } else {
        console.warn('Invalid challenge response:', response);
      }
    } catch (error) {
      console.error('Error fetching challenge:', error.response?.data || error.message);
      if (isMountedRef.current) {
        setMessage('Error loading challenge data');
      }
    } finally {
      if (isMountedRef.current && !silent) {
        setLoadingChallenge(false);
      }
    }
  }, [challengeId, fetchRolls]);

  useEffect(() => {
    if (gameState !== 'rolling') {
      setTurnSecondsLeft(null);
      return;
    }
    if (!turnDeadlineAt) {
      setTurnSecondsLeft(null);
      return;
    }

    const tick = () => {
      const msLeft = Math.max(0, Number(turnDeadlineAt) - Date.now());
      setTurnSecondsLeft(Math.ceil(msLeft / 1000));
    };

    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [gameState, turnDeadlineAt]);

  const formatSeconds = (seconds) => {
    if (seconds == null) return '';
    const s = Math.max(0, seconds);
    const mm = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const allReady = React.useMemo(() => {
    if (!challenge) return false;
    if (challenge.current_players !== challenge.max_players) return false;
    const participantIds = [challenge.creator_id, ...(challenge.opponent_ids || [])];
    return participantIds.length === challenge.current_players && participantIds.every(id => readyPlayerIds.includes(id));
  }, [challenge, readyPlayerIds]);

  const isReady = React.useMemo(() => {
    return readyPlayerIds.includes(user.id);
  }, [readyPlayerIds, user.id]);

  const handleReadyUp = async () => {
    try {
      setLoading(true);
      await multiplayerAPI.setReady(challengeId);
      // Refresh to pick up updated ready list
      await fetchChallenge();
    } catch (error) {
      console.error('❌ Error setting ready:', error.response?.data || error.message);
      setMessage(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!socket || !user?.id) return;

    const onConnect = () => {
      if (wasDisconnectedRef.current) {
        setConnectionBanner('Reconnected. Syncing…');
        // Re-sync state from REST to rebuild trust
        fetchChallenge();
        setTimeout(() => {
          if (isMountedRef.current) setConnectionBanner('');
        }, 2500);
      }
      wasDisconnectedRef.current = false;
    };

    const onDisconnect = () => {
      wasDisconnectedRef.current = true;
      setConnectionBanner('Reconnecting…');
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    // Join the challenge room on Socket.IO and notify server that this player is present
    const roomName = `challenge-${challengeId}`;
    socket.emit('join-room', {
      room: roomName,
      challengeId: parseInt(challengeId),
      userId: user.id
    });
    socket.emit('multiplayer:join-challenge', {
      challengeId: parseInt(challengeId),
      userId: user.id
    });
    console.log(`Socket: Joining room ${roomName}`);

    // Listen for players joining
    const onPlayerJoined = (data) => {
      console.log('Player joined:', data);
      if (data.challengeId === parseInt(challengeId)) {
        if (gameStateRef.current === 'complete') {
          return;
        }
        const joinedName = data.username || getDisplayName(data.userId, `Player ${data.userId || 'Unknown'}`);
        setMessage(`${joinedName} joined the challenge!`);
        setTimeout(() => {
          if (isMountedRef.current) fetchChallenge();
        }, 500);
      }
    };

    // Listen for game start
    const onGameStarted = (data) => {
      console.log('Game started:', data);
      if (data.challengeId === parseInt(challengeId) && isMountedRef.current) {
        if (Array.isArray(data.playerOrder)) {
          setPlayerOrder(data.playerOrder);
        }
        setGameState('rolling');
        setPlayerRolls({});
        setMyScore(null);
        setDiceValues([]);
        const nextTurn = typeof data.currentTurnUserId !== 'undefined'
          ? data.currentTurnUserId
          : (Array.isArray(data.playerOrder) ? data.playerOrder[0] : null);
        setCurrentPlayerTurn(nextTurn || null);
        setTurnDeadlineAt(data.turnDeadlineAt ?? null);
        setMessage(data.message || 'Game started! Creator rolls first.');
      }
    };

    const onReadyUpdate = (data) => {
      if (data.challengeId === parseInt(challengeId) && isMountedRef.current) {
        if (Array.isArray(data.readyPlayerIds)) {
          setReadyPlayerIds(data.readyPlayerIds);
        } else {
          fetchChallenge();
        }
      }
    };

    // Listen for other players' rolls
    const onRollUpdate = (data) => {
      console.log('Player rolled:', data);
      if (data.challengeId === parseInt(challengeId) && isMountedRef.current) {
        setPlayerRolls(prev => ({
          ...prev,
          [data.userId]: {
            score: data.score,
            diceValues: data.diceValues
          }
        }));
        if (typeof data.nextTurnUserId !== 'undefined') {
          setCurrentPlayerTurn(data.nextTurnUserId || null);
        }
        if (typeof data.turnDeadlineAt !== 'undefined') {
          setTurnDeadlineAt(data.turnDeadlineAt);
        }
        if (typeof data.nextTurnUserId !== 'undefined' && data.nextTurnUserId === null) {
          fetchChallenge(true);
        }
      }
    };

    // Listen for game results
    const onGameComplete = (data) => {
      console.log('Game complete:', data);
      if (data.challengeId === parseInt(challengeId) && isMountedRef.current) {
        setGameState('complete');
        setTurnDeadlineAt(null);
        setResults({
          placement: data.placement.map((p, idx) => ({
            rank: p.rank || idx + 1,
            userId: p.userId,
            username: p.username,
            score: p.score,
            diceValues: p.diceValues,
            prize: data.prizes?.[p.userId] ?? data.prizes?.[String(p.userId)] ?? 0
          }))
        });
        setCurrentPlayerTurn(null);
        setPlayerRolls({});
        setMessage('Game ended. Results below.');
      }
    };

    const onGameCancelled = (data) => {
      if (data.challengeId === parseInt(challengeId) && isMountedRef.current) {
        setMessage(data.message || 'Game cancelled.');
        setGameState('lobby');
        setTurnDeadlineAt(null);
        setResults(null);
        setPlayerRolls({});
        setCurrentPlayerTurn(null);
        // Go back to dashboard after a short moment so user sees message
        setTimeout(() => {
          if (isMountedRef.current) navigate('/dashboard');
        }, 1200);
      }
    };

    socket.on('multiplayer:player-joined', onPlayerJoined);
    socket.on('multiplayer:game-started', onGameStarted);
    socket.on('multiplayer:roll-update', onRollUpdate);
    socket.on('multiplayer:game-complete', onGameComplete);
    socket.on('multiplayer:game-cancelled', onGameCancelled);
    socket.on('multiplayer:ready-update', onReadyUpdate);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('multiplayer:player-joined', onPlayerJoined);
      socket.off('multiplayer:game-started', onGameStarted);
      socket.off('multiplayer:roll-update', onRollUpdate);
      socket.off('multiplayer:game-complete', onGameComplete);
      socket.off('multiplayer:game-cancelled', onGameCancelled);
      socket.off('multiplayer:ready-update', onReadyUpdate);
    };
  }, [socket, user?.id, challengeId, fetchChallenge, navigate]);

  // Fetch initial challenge data as soon as the component mounts / challengeId changes
  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  // This useEffect was removed to prevent the game from starting automatically.
  // The game will now be started by the creator.

  // Fetch existing rolls when game state changes to rolling
  useEffect(() => {
    if (gameState === 'rolling' && isMountedRef.current) {
      fetchRolls();
    }
  }, [gameState, fetchRolls]);

  // Fallback sync: poll while rolling so players don't need manual refresh
  useEffect(() => {
    if (gameState !== 'rolling') return;
    const id = setInterval(() => {
      if (isMountedRef.current) {
        fetchRolls();
        fetchChallenge(true);
      }
    }, 700);
    return () => clearInterval(id);
  }, [gameState, fetchRolls, fetchChallenge]);

  // Fallback sync in lobby so non-creator clients auto-transition when creator starts the game
  useEffect(() => {
    if (gameState !== 'lobby') return;
    const id = setInterval(() => {
      if (isMountedRef.current) {
        fetchChallenge(true);
      }
    }, 1500);
    return () => clearInterval(id);
  }, [gameState, fetchChallenge]);

  const rollDice = async () => {
    try {
      if (currentPlayerTurn !== null && currentPlayerTurn !== user.id) {
        setMessage('Please wait for your turn to pick.');
        return;
      }
      if (playerOrder.length === 0) {
        setMessage('Waiting for all players to be ready.');
        return;
      }
      setLoading(true);

      // Request a roll from backend (server generates dice to prevent cheating)
      const response = await multiplayerAPI.submitRoll(challengeId);
      
      if (response && response.data && response.data.success) {
        console.log('✓ Roll submitted successfully');

        const serverDice = response.data.diceValues || response.data.roll?.dice_values || [];
        const serverScore = typeof response.data.score !== 'undefined'
          ? response.data.score
          : response.data.roll?.score;

        setIsRevealing(true);
        setDiceValues(serverDice);
        setMyScore(serverScore);
        if (revealTimerRef.current) {
          clearTimeout(revealTimerRef.current);
        }
        revealTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setIsRevealing(false);
        }, 700);

        // The backend now handles broadcasting the roll, so no need to emit here.

        // Check if game is complete
        if (typeof response.data.nextTurnUserId !== 'undefined') {
          setCurrentPlayerTurn(response.data.nextTurnUserId || null);
        }

        if (response.data.gameComplete) {
          console.log('Game complete! All players have rolled.');
          // Fetch the finalized challenge so prize_distribution is available even if socket events are missed
          await fetchChallenge();
        } else {
          setMessage('Numbers picked! Waiting for other players...');
        }
      } else {
        console.warn('Unexpected response format:', response);
        setMessage('Error submitting numbers');
      }
    } catch (error) {
      console.error('Error rolling dice:', error.response?.data || error.message);
      if (error.response?.data?.error === 'You have already rolled in this game') {
        setMessage('You have already picked in this game!');
      } else if (error.response?.data?.error === 'Not your turn to roll yet') {
        setMessage('Not your turn yet. Please wait.');
      } else {
        setMessage(`Error: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartGame = async () => {
    try {
      setLoading(true);
      console.log(`▶️ Starting challenge ${challengeId}`);
      const response = await multiplayerAPI.startChallenge(challengeId);
      console.log('✓ Start challenge response:', response);
      const data = response?.data || {};
      const fallbackOrder = challenge
        ? [challenge.creator_id, ...(challenge.opponent_ids || [])]
        : playerOrder;
      const updatedOrder = Array.isArray(data.playerOrder) ? data.playerOrder : fallbackOrder;
      setPlayerOrder(updatedOrder);
      const firstTurn = data.currentTurnUserId ?? (updatedOrder.length > 0 ? updatedOrder[0] : null);
      setCurrentPlayerTurn(firstTurn || null);
      setPlayerRolls({});
      setMyScore(null);
      setDiceValues([]);
      setGameState('rolling');
      setMessage('Game starting! Creator rolls first.');
    } catch (error) {
      console.error('❌ Error starting game:', error.response?.data || error.message);
      const notReady = error.response?.data?.notReady;
      if (Array.isArray(notReady) && notReady.length > 0) {
        const names = notReady.map((id) => getDisplayName(id, `Player ${id}`)).join(', ');
        setMessage(`Error starting game: ${error.response?.data?.error || error.message}. Not ready: ${names}`);
      } else {
        setMessage(`Error starting game: ${error.response?.data?.error || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGame = async () => {
    try {
      setLoading(true);
      await multiplayerAPI.cancelChallenge(challengeId);
      setMessage('Game cancelled. Wagers refunded.');
      navigate('/multiplayer');
    } catch (error) {
      console.error('❌ Error cancelling game:', error.response?.data || error.message);
      setMessage(`Error cancelling game: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="multiplayer-game">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2>🎮 Lucky Number Challenge</h2>
        <button
          className="btn-secondary"
          onClick={() => navigate('/multiplayer')}
        >
          Back
        </button>
      </div>

      {connectionBanner && (
        <div className="message" style={{ background: '#eef6ff', border: '1px solid #cfe3ff', color: '#0b4a7a' }}>
          {connectionBanner}
        </div>
      )}
      
      {message && (
        <div className="message success">
          {message}
        </div>
      )}

      {loadingChallenge && (
        <div className="loading-state">
          <p>Loading challenge...</p>
          <div className="spinner"></div>
        </div>
      )}

      {!loadingChallenge && !challenge && (
        <div className="error-state">
          <p>❌ Could not load challenge. Please go back and try again.</p>
        </div>
      )}

      {gameState === 'lobby' && challenge && (
        <div className="lobby-state">
          <div className="challenge-info">
            <h3>Waiting for Players</h3>
            <p>
              <strong>Mode:</strong>{' '}
              <span className={`lucky-mode ${isBettingGame ? 'betting' : 'friendly'}`}>
                {isBettingGame ? '⚠️ Betting Game' : '✅ Friendly Game'}
              </span>
            </p>
            <p>
              <strong>Wager:</strong>{' '}
              <span className={`lucky-wager ${isBettingGame ? 'betting' : 'friendly'}`}>
                ${challenge.wager}
              </span>
            </p>
            <p><strong>Players:</strong> {challenge.current_players} / {challenge.max_players}</p>
            <p><strong>Prize Pool:</strong> ${challenge.wager * challenge.current_players}</p>
          </div>

          <div className="player-list">
            <h4>Players in Challenge</h4>
            <div className="players">
              {playerOrder.slice(0, challenge.current_players).map((playerId, idx) => (
                <div key={playerId || idx} className="player-slot">
                  <div className="player-badges">
                    {idx === 0 && <span className="badge">Creator</span>}
                    {readyPlayerIds.includes(playerId) && <span className="badge">Ready</span>}
                  </div>
                  <span className="player-name">{getDisplayName(playerId, `Player ${idx + 1}`)}</span>
                </div>
              ))}
              {Array.from({ length: challenge.max_players - challenge.current_players }).map((_, idx) => (
                <div key={`empty-${idx}`} className="player-slot empty">
                  <span>Waiting...</span>
                </div>
              ))}
            </div>
          </div>

          {challenge.creator_id === user.id && challenge.current_players === challenge.max_players && (
            <div className="start-spotlight">
              <div className="start-spotlight__title">All players are here</div>
              <div className="start-spotlight__sub">Start the challenge from the center when everyone is ready.</div>
              <button 
                className="btn-primary"
                onClick={handleStartGame}
                disabled={loading || !allReady}
              >
                Start Game Now
              </button>
            </div>
          )}

          {challenge.current_players === challenge.max_players && !isReady && (
            <button
              className="btn-secondary"
              onClick={handleReadyUp}
              disabled={loading}
              style={{ marginLeft: 8 }}
            >
              I’m Ready
            </button>
          )}

          {challenge.current_players === challenge.max_players && !allReady && (
            <p className="info-text">Waiting for all players to press Ready…</p>
          )}

          {challenge.creator_id === user.id && challenge.game_status === 'waiting' && (
            <button
              className="btn-secondary lucky-action-btn"
              onClick={handleCancelGame}
              disabled={loading}
              style={{ marginLeft: 8 }}
            >
              Cancel Game
            </button>
          )}

          {challenge.creator_id === user.id && challenge.current_players < challenge.max_players && (
            <p className="info-text">Waiting for {challenge.max_players - challenge.current_players} more player(s)...</p>
          )}

          {challenge.creator_id !== user.id && (
            <p className="info-text">Waiting for the creator to start the game...</p>
          )}
        </div>
      )}

      {gameState === 'rolling' && (
        <div className="rolling-state">
          <h3>✨ Pick your lucky numbers!</h3>

          {playerOrder.length > 0 && (
            <p className="info-text">
              {currentPlayerTurn === null
                ? 'All rolls submitted. Waiting for results...'
                : isMyTurn
                  ? 'It\'s your turn to pick!'
                  : currentTurnIndex >= 0
                    ? `Waiting for ${getDisplayName(currentPlayerTurn, `Player ${currentTurnIndex + 1}`)} to pick...`
                    : 'Waiting for the next player to reconnect...'}
            </p>
          )}

          {currentPlayerTurn !== null && turnSecondsLeft != null && (
            <p className="info-text" style={{ marginTop: 6 }}>
              Auto-roll in {formatSeconds(turnSecondsLeft)}
            </p>
          )}
          
          {myScore === null ? (
            <>
              <button 
                className="btn-large lucky-action-btn"
                onClick={rollDice}
                disabled={loading || !isMyTurn}
              >
                🎯 Pick Numbers
              </button>
              {!isMyTurn && (
                <p className="info-text">Only the active player can pick right now.</p>
              )}

              {challenge?.creator_id === user.id && Object.keys(playerRolls).length === 0 && (
                <button
                  className="btn-secondary lucky-action-btn"
                  onClick={handleCancelGame}
                  disabled={loading}
                  style={{ marginTop: 8 }}
                >
                  Cancel Game
                </button>
              )}
            </>
          ) : (
            <div className="dice-result">
              <div className="dice-display">
                {diceValues.map((value, idx) => (
                  <div key={idx} className={`die ${isRevealing ? 'revealing' : ''}`}>
                    {isRevealing ? '•' : value}
                  </div>
                ))}
              </div>
              {isRevealing ? (
                <h2>Revealing...</h2>
              ) : (
                <>
                  <h2>Your Score: {myScore}</h2>
                  <p>Waiting for other players to pick...</p>
                </>
              )}
            </div>
          )}

          {playerOrder.length > 0 && (
            <div className="scores-board">
              <h4>Turn Order</h4>
              {playerOrder.map((playerId, idx) => {
                const roll = playerRolls[playerId];
                const isCurrent = currentPlayerTurn === playerId;
                const label = getDisplayName(playerId, `Player ${idx + 1}`);
                return (
                  <div key={playerId || idx} className={`score-item ${isCurrent ? 'active' : ''}`}>
                    <span>{label}</span>
                    <span className="score">
                      {roll
                        ? (playerId === user.id ? `${roll.score} pts` : 'Rolled')
                        : isCurrent
                          ? 'Rolling now...'
                          : 'Waiting'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {gameState === 'complete' && results && (
        <div className="results-state">
          <h2>🏆 Game Results</h2>
          
          <div className="leaderboard">
            {results.placement && results.placement.map((player, idx) => (
              <div key={idx} className={`placement rank-${player.rank}`}>
                <span className="rank">#{player.rank}</span>
                <span className="name">{player.username || `Player ${player.userId}`}</span>
                <span className="prize">Won: {player.prize || 0} wikipenny</span>
                <span className="score">{player.score} pts</span>
                <div className="dice-small">
                  {player.diceValues && player.diceValues.map((die, dIdx) => (
                    <span key={dIdx} className="die-small">{die}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="result-summary">
            <p>Game Complete! Thanks for playing!</p>
            <button className="btn-primary" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiplayerGame;

